/**
 * Save-play ledger — the outcome-proof layer that turns a launched save
 * play into a LEDGER-BACKED saved-ARR claim (or, honestly, into nothing).
 *
 * attributeOutcome replays reality against the play's sealed outcome
 * contract via the SHIPPED outcome verifier (injected, never forked):
 *
 *   • VERIFIED    → recovered=true, savedArr = the contract's real dollars
 *                   at stake, an 'approved' value-ledger entry attributing
 *                   the save, and a rollup (via the shipped valueRollup)
 *                   proving the entry lands as dollarImpact.
 *   • MISSED /    → recovered=false, savedArr=0, NO ledger entry. A miss —
 *     INCONCLUSIVE  or a still-open window — earns nothing. Unknown is
 *                   never assumed positive.
 *
 * Deliberately PURE: the verifier context (activity, health(t), clock) and
 * every collaborator arrive as arguments. No store, no network.
 */

import type { OutcomeContract } from '../agent/outcomeContract';
import type { OutcomeResult, OutcomeVerdict, VerifyContext } from '../agent/outcomeVerifier';
import type { DateRange, LedgerEntry, ValueEntry, ValueRollup } from '../agent/valueRollup';
import type { CommittedPlay } from './savePlayEngine';

/** Injected deps for {@link attributeOutcome} — the shipped closers. */
export interface AttributeOutcomeDeps {
  /** agent/outcomeVerifier.verifyOutcome — the one true verdict engine. */
  outcomeVerifier: {
    verify: (contract: OutcomeContract, ctx: VerifyContext) => OutcomeResult;
  };
  /** agent/valueLedger.MINUTES_SAVED_BY_KIND — transparent minute estimates. */
  valueLedger: { minutesSavedByKind: Record<string, number> };
  /** agent/valueRollup.rollupValue — proves the entry lands in the rollup. */
  valueRollup: {
    rollup: (entries: ValueEntry[], ledger: LedgerEntry[], range: DateRange) => ValueRollup;
  };
  /** The committed-play registry the playId resolves against. */
  plays: CommittedPlay[];
}

/** The honest attribution for one play. */
export interface SaveOutcome {
  playId: string;
  /** True only when the verifier rendered VERIFIED on the sealed contract. */
  recovered: boolean;
  /** Real ARR retained — 0 unless recovered. Never invented. */
  savedArr: number;
  /** Click-verifiable references: the contract + the verifier's evidence. */
  evidenceRefs: string[];
  verdict: OutcomeVerdict | 'NOT_FOUND';
  /** The value-ledger attribution entry — null unless recovered. */
  ledgerEntry: ValueEntry | null;
  /** Rollup over the attribution entry (dollarImpact === savedArr) — null unless recovered. */
  rollup: ValueRollup | null;
}

/**
 * Link a play's verified recovery to saved ARR. Finds the committed play
 * (by play id or batch id), replays the verifier context against its sealed
 * contract, and attributes:
 *
 *   recovered=true  → savedArr = contract.dollarsAtStake, evidenceRefs from
 *                     the verifier, plus a ledger entry + rollup proof;
 *   otherwise       → recovered=false, savedArr=0, no ledger entry.
 *
 * Pure: same inputs → same attribution. Nothing is written here — the
 * caller persists the ledger entry through the existing ledger API.
 */
export function attributeOutcome(
  playId: string,
  verifier: VerifyContext,
  deps: AttributeOutcomeDeps,
): SaveOutcome {
  const committed = deps.plays.find(
    (c) => c.play.id === playId || c.batchId === playId,
  );
  if (!committed) {
    return {
      playId,
      recovered: false,
      savedArr: 0,
      evidenceRefs: [`no committed play matches '${playId}'`],
      verdict: 'NOT_FOUND',
      ledgerEntry: null,
      rollup: null,
    };
  }

  const contract = committed.play.contract;
  const result = deps.outcomeVerifier.verify(contract, verifier);
  const recovered = result.verdict === 'VERIFIED';
  const savedArr = recovered ? contract.dollarsAtStake : 0;
  const evidenceRefs = [`contract:${contract.proposalId}`, ...result.evidence];

  if (!recovered) {
    return {
      playId,
      recovered: false,
      savedArr: 0,
      evidenceRefs,
      verdict: result.verdict,
      ledgerEntry: null,
      rollup: null,
    };
  }

  // Transparent minutes: the shipped per-kind estimates over the play's
  // real task graph — auditable, never a black box.
  const minutesSaved = committed.play.tasks.reduce(
    (sum, t) => sum + (deps.valueLedger.minutesSavedByKind[t.actionType] ?? 0),
    0,
  );

  const at = new Date(verifier.now).toISOString();
  const ledgerEntry: ValueEntry = {
    id: `save-arr:${committed.play.id}`,
    agent: 'save-play-engine',
    at,
    status: 'approved',
    minutesSaved,
    dollarImpact: savedArr,
  };

  // Proof, not vibes: run the SHIPPED rollup over the single attribution
  // entry — its dollarImpact must equal savedArr in the range containing it.
  const rollup = deps.valueRollup.rollup([ledgerEntry], [], { from: at, to: at });

  return {
    playId,
    recovered: true,
    savedArr,
    evidenceRefs,
    verdict: result.verdict,
    ledgerEntry,
    rollup,
  };
}
